"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { DataTable, type Column } from "../DataTable";
import { useERP, formatBDT, formatNum, type ID } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export interface MasterDataModuleProps {
  onNavigate: (pageId: string) => void;
}

export function MasterDataModule({ onNavigate }: MasterDataModuleProps) {
  const erp = useERP();
  const [tab, setTab] = React.useState("products");
  const tabs: SimpleTab[] = [
    { value: "products", label: "Products", count: erp.products.length },
    { value: "customers", label: "Customers", count: erp.customers.length },
    { value: "suppliers", label: "Suppliers", count: erp.suppliers.length },
    { value: "warehouses", label: "Warehouses", count: erp.warehouses.length },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">Master Data</h1>
        <p className="text-muted-foreground mt-1 text-sm">CRUD for products, customers, suppliers and warehouses.</p>
      </div>
      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />

      {tab === "products" && <ProductsTab onNavigate={onNavigate} />}
      {tab === "customers" && <CustomersTab onNavigate={onNavigate} />}
      {tab === "suppliers" && <SuppliersTab onNavigate={onNavigate} />}
      {tab === "warehouses" && <WarehousesTab onNavigate={onNavigate} />}
    </div>
  );
}

// ===== Reusable delete confirmation dialog =====
function DeleteConfirmDialog({ open, onOpenChange, onConfirm, label, name }: {
  open: boolean; onOpenChange: (v: boolean) => void; onConfirm: () => void; label: string; name: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="size-5 text-destructive" /> Confirm Delete</DialogTitle>
          <DialogDescription>Are you sure you want to delete <strong className="text-foreground">{name}</strong> ({label})? This action cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={() => { onConfirm(); onOpenChange(false); }}><Trash2 className="size-4 mr-1" /> Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductsTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const deleteTarget = erp.products.find(p => p.id === deleteId);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Products</CardTitle>
          <Button size="sm" onClick={() => onNavigate("md-products-new")}><Plus className="size-4" /> New Product</Button>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "sku", header: "SKU", sortable: true, render: r => <span className="font-mono text-xs">{r.sku}</span> },
              { key: "name", header: "Product", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
              { key: "category", header: "Category" },
              { key: "brand", header: "Brand" },
              { key: "costPrice", header: "Cost", align: "right", sortable: true, render: r => formatBDT(r.costPrice) },
              { key: "salePrice", header: "Sale", align: "right", render: r => formatBDT(r.salePrice) },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="size-7" title="View" onClick={() => toast({ title: r.name, description: `${r.category} · ${r.brand} · ${r.uom} · Stock: ${erp.getStock(r.id)}` })}><Eye className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7" title="Edit" onClick={() => onNavigate(`md-products-new?edit=${r.id}`)}><Pencil className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7 text-destructive" title="Delete" onClick={() => setDeleteId(r.id)}><Trash2 className="size-3.5" /></Button>
                </div>
              ) },
            ] as Column<typeof erp.products[number]>[]}
            data={erp.products}
            rowKey={r => r.id}
            searchKeys={["sku", "name", "category", "brand"]}
          />
        </CardContent>
      </Card>
      <DeleteConfirmDialog
        open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}
        label="Product" name={deleteTarget?.name ?? ""}
        onConfirm={() => { if (deleteId) { const name = deleteTarget?.name; erp.deleteProduct(deleteId); toast({ title: "Product Deleted", description: `${name} has been removed.`, variant: "destructive" }); } }}
      />
    </>
  );
}

function CustomersTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const deleteTarget = erp.customers.find(c => c.id === deleteId);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Customers</CardTitle>
          <Button size="sm" onClick={() => onNavigate("md-customers-new")}><Plus className="size-4" /> New Customer</Button>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
              { key: "name", header: "Customer", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
              { key: "type", header: "Type" },
              { key: "zone", header: "Zone" },
              { key: "creditLimit", header: "Credit Limit", align: "right", render: r => formatBDT(r.creditLimit) },
              { key: "outstanding", header: "Outstanding", align: "right", sortable: true, render: r => <span className={r.outstanding > 0 ? "text-destructive font-medium" : ""}>{formatBDT(r.outstanding)}</span> },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : r.status === "Blocked" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="size-7" title="View" onClick={() => toast({ title: r.name, description: `${r.type} · ${r.zone} · Credit Limit: ${formatBDT(r.creditLimit)}` })}><Eye className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7" title="Edit" onClick={() => onNavigate(`md-customers-new?edit=${r.id}`)}><Pencil className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7 text-destructive" title="Delete" onClick={() => setDeleteId(r.id)}><Trash2 className="size-3.5" /></Button>
                </div>
              ) },
            ] as Column<typeof erp.customers[number]>[]}
            data={erp.customers}
            rowKey={r => r.id}
            searchKeys={["code", "name", "type", "zone"]}
          />
        </CardContent>
      </Card>
      <DeleteConfirmDialog
        open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}
        label="Customer" name={deleteTarget?.name ?? ""}
        onConfirm={() => { if (deleteId) { const name = deleteTarget?.name; erp.deleteCustomer(deleteId); toast({ title: "Customer Deleted", description: `${name} has been removed.`, variant: "destructive" }); } }}
      />
    </>
  );
}

function SuppliersTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const deleteTarget = erp.suppliers.find(s => s.id === deleteId);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Suppliers</CardTitle>
          <Button size="sm" onClick={() => onNavigate("md-suppliers-new")}><Plus className="size-4" /> New Supplier</Button>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
              { key: "name", header: "Supplier", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
              { key: "category", header: "Category" },
              { key: "contactPerson", header: "Contact" },
              { key: "leadTimeDays", header: "Lead Time", align: "right" },
              { key: "rating", header: "Rating", align: "right", render: r => `★ ${r.rating.toFixed(1)}` },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="size-7" title="View" onClick={() => toast({ title: r.name, description: `${r.category} · ${r.contactPerson} · Lead ${r.leadTimeDays}d` })}><Eye className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7" title="Edit" onClick={() => onNavigate(`md-suppliers-new?edit=${r.id}`)}><Pencil className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7 text-destructive" title="Delete" onClick={() => setDeleteId(r.id)}><Trash2 className="size-3.5" /></Button>
                </div>
              ) },
            ] as Column<typeof erp.suppliers[number]>[]}
            data={erp.suppliers}
            rowKey={r => r.id}
            searchKeys={["code", "name", "category"]}
          />
        </CardContent>
      </Card>
      <DeleteConfirmDialog
        open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}
        label="Supplier" name={deleteTarget?.name ?? ""}
        onConfirm={() => { if (deleteId) { const name = deleteTarget?.name; erp.deleteSupplier(deleteId); toast({ title: "Supplier Deleted", description: `${name} has been removed.`, variant: "destructive" }); } }}
      />
    </>
  );
}

function WarehousesTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const deleteTarget = erp.warehouses.find(w => w.id === deleteId);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Warehouses</CardTitle>
          <Button size="sm" onClick={() => onNavigate("md-warehouses-new")}><Plus className="size-4" /> New Warehouse</Button>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
              { key: "name", header: "Warehouse", sortable: true },
              { key: "type", header: "Type" },
              { key: "branch", header: "Branch" },
              { key: "capacity", header: "Capacity", align: "right", render: r => formatNum(r.capacity) },
              { key: "utilization", header: "Utilization", align: "right", render: r => <span className={r.utilization > 80 ? "text-destructive" : ""}>{r.utilization}%</span> },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="size-7" title="View" onClick={() => toast({ title: r.name, description: `${r.type} · ${r.branch} · Stock: ${erp.getStock("", r.id)} units` })}><Eye className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7" title="Edit" onClick={() => onNavigate(`md-warehouses-new?edit=${r.id}`)}><Pencil className="size-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="size-7 text-destructive" title="Delete" onClick={() => setDeleteId(r.id)}><Trash2 className="size-3.5" /></Button>
                </div>
              ) },
            ] as Column<typeof erp.warehouses[number]>[]}
            data={erp.warehouses}
            rowKey={r => r.id}
            searchKeys={["code", "name", "branch"]}
          />
        </CardContent>
      </Card>
      <DeleteConfirmDialog
        open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}
        label="Warehouse" name={deleteTarget?.name ?? ""}
        onConfirm={() => { if (deleteId) { const name = deleteTarget?.name; erp.deleteWarehouse(deleteId); toast({ title: "Warehouse Deleted", description: `${name} has been removed.`, variant: "destructive" }); } }}
      />
    </>
  );
}
